import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DndContext, DragOverlay, PointerSensor, closestCorners, useSensor, useSensors } from '@dnd-kit/core'

import TasksBoardToolbar from 'pages/parts/tasks/TasksBoardToolbar'
import TasksBoardColumn from 'pages/parts/tasks/TasksBoardColumn'
import TasksBoardCard from 'pages/parts/tasks/TasksBoardCard'
import ModalCreateUpdate from 'pages/parts/tasks/ModalCreateUpdate'
import ModalTaskStatus from 'pages/parts/tasks/ModalTaskStatus'
import ModalTaskDetail from 'pages/parts/tasks/ModalTaskDetail'
import DeleteModal from 'pages/parts/DeleteModal'

import { returnSign, notify } from 'helpers/helpers'
import { httpOk, GET, POST, PUT, DELETE, FILE, FILE_D } from 'helpers/api'

const COLUMN_PAGE_SIZE = 20
// Статусы динамические, поэтому колонку «Продленный» определяем по value, а не по id
const DEADLINE_PROMPT_VALUES = ['postponed', 'extended', 'prolonged', 'deferred', 'delayed']

function getDefaultItem() {
	return {
		name: '',
		description: '',
		deadline: '',
		category_id: null,
		client_login: null,
		user_ids: [],
		// pos_id уходит на бэк, pos — выбранная опция для селекта
		pos_id: null,
		pos: null,
		// На подключении точки в системе ещё нет — данные клиента вводятся руками
		client_phone: '',
		director_phone: '',
		address: '',
		direction: '',
		lead_source: '',
	}
}

function getInitialFilterData() {
	return {
		search: '',
		user_id: null,
		category_id: null,
		pos: null,
		is_overdue: null,
		// null — все задачи, 'system' — только созданные кроном, 'manual' — только руками
		source: null,
		deadline: null,
		deadline_month: null,
	}
}

function TasksBoardView({
	apiPrefix,
	categoriesPath = null,
	titleKey = 'tasks',
	currentUserLogin = null,
	canManageTask = () => true,
	// Автор задачи распоряжается ей целиком, соисполнитель — только правит поля
	canEditTask = null,
}) {
	const { t } = useTranslation()

	const canEdit = canEditTask || canManageTask

	// справочники
	const [statuses, setStatuses] = useState([])
	const [categories, setCategories] = useState([])
	const [partners, setPartners] = useState([])

	// доска
	const [columns, setColumns] = useState([])
	const [deadlineCounts, setDeadlineCounts] = useState([])
	const [boardTotal, setBoardTotal] = useState(0)

	// фильтры
	const [filterData, setFilterData] = useState(getInitialFilterData())
	const [activeChip, setActiveChip] = useState('all')
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)

	// drag & drop
	const [activeCard, setActiveCard] = useState(null)
	const [movingIds, setMovingIds] = useState(new Set())

	// протяжка доски мышью
	const [isPanning, setIsPanning] = useState(false)
	const boardRef = useRef(null)
	const panRef = useRef(null)

	// модалки
	const [modalCreateUpdate, setModalCreateUpdate] = useState(false)
	const [selectedItem, setSelectedItem] = useState(getDefaultItem())
	const [modalTaskStatus, setModalTaskStatus] = useState(false)
	const [pendingMove, setPendingMove] = useState(null)
	const [modalDetail, setModalDetail] = useState(false)
	const [detailItem, setDetailItem] = useState({})
	const [modalDelete, setModalDelete] = useState(false)

	const filterVersionRef = useRef(0)
	// onScroll стреляет чаще, чем успевает примениться setColumns, поэтому блокируем по ref
	const loadingColumnsRef = useRef(new Set())

	// Автор всегда исполнитель. Берём id из справочника, а не из redux, — так мы
	// никогда не отправим на бэк id, которого нет в его ветке (у админа его тут не будет)
	const currentUser = currentUserLogin
		? partners.find(item => String(item.login).toLowerCase() === String(currentUserLogin).toLowerCase()) || null
		: null

	const sensors = useSensors(
		useSensor(PointerSensor, {
			// Тащить только после сдвига на 5px, иначе не работают клик по карточке и кнопки
			activationConstraint: { distance: 5 },
		})
	)

	function buildParams(nextFilterData = filterData, extra = {}) {
		var urlParams = ""

		if (nextFilterData.search)
			urlParams += returnSign(urlParams) + 'search=' + encodeURIComponent(nextFilterData.search)
		if (nextFilterData.user_id)
			urlParams += returnSign(urlParams) + 'user_id=' + nextFilterData.user_id
		if (nextFilterData.category_id)
			urlParams += returnSign(urlParams) + 'category_id=' + nextFilterData.category_id
		if (nextFilterData.pos)
			urlParams += returnSign(urlParams) + 'pos_id=' + nextFilterData.pos.id
		if (nextFilterData.is_overdue)
			urlParams += returnSign(urlParams) + 'is_overdue=1'
		if (nextFilterData.source)
			urlParams += returnSign(urlParams) + 'source=' + nextFilterData.source
		if (nextFilterData.deadline)
			urlParams += returnSign(urlParams) + 'deadline=' + nextFilterData.deadline
		if (nextFilterData.deadline_month)
			urlParams += returnSign(urlParams) + 'deadline_month=' + nextFilterData.deadline_month

		for (const key in extra) {
			if (extra[key] === null || extra[key] === undefined) continue
			urlParams += returnSign(urlParams) + key + '=' + extra[key]
		}

		return urlParams
	}

	function mergeColumns(statusList, responseColumns) {
		// Колонки рисуем из справочника статусов, чтобы пустые статусы тоже отображались
		const source = (statusList && statusList.length > 0)
			? statusList
			: (responseColumns || [])

		return [...source]
			.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
			.map((status) => {
				const found = (responseColumns || []).find(item => item.id === status.id)
				return {
					'id': status.id,
					'value': status.value,
					'name': status.name,
					'color': status.color,
					'sort_order': status.sort_order,
					'is_final': status.is_final,
					'total': found?.total || 0,
					'data': found?.data || [],
					'page': 0,
					'loading': false,
				}
			})
	}

	async function loadBoard(nextFilterData = filterData, statusList = statuses) {
		const version = ++filterVersionRef.current
		const url = `${apiPrefix}/tasks` + buildParams(nextFilterData, { 'size': COLUMN_PAGE_SIZE })

		const response = await GET(url, {}, { loader: true })
		if (!httpOk(response) || version !== filterVersionRef.current) return

		loadingColumnsRef.current.clear()
		setColumns(mergeColumns(statusList, response.data?.data))
		setDeadlineCounts(response.data?.deadline_counts || [])
		setBoardTotal(response.data?.total || 0)
		setDropdownIsOpen(false)
	}

	async function loadColumnPage(column) {
		if (column.loading || loadingColumnsRef.current.has(column.id)) return
		if ((column.data?.length || 0) >= (column.total || 0)) return

		const version = filterVersionRef.current
		const nextPage = (column.page || 0) + 1

		loadingColumnsRef.current.add(column.id)
		setColumns(prev => prev.map(item => item.id === column.id ? { ...item, 'loading': true } : item))

		const url = `${apiPrefix}/tasks` + buildParams(filterData, {
			'task_status_id': column.id,
			'page': nextPage,
			'size': COLUMN_PAGE_SIZE,
		})
		const response = await GET(url, {}, { loader: false })
		loadingColumnsRef.current.delete(column.id)

		if (!httpOk(response) || version !== filterVersionRef.current) {
			setColumns(prev => prev.map(item => item.id === column.id ? { ...item, 'loading': false } : item))
			return
		}

		const payload = (response.data?.data || []).find(item => item.id === column.id) || response.data?.data?.[0]
		const incoming = payload?.data || []

		setColumns(prev => prev.map(item => {
			if (item.id !== column.id) return item
			const fresh = incoming.filter(task => !item.data.some(exist => exist.id === task.id))
			return {
				...item,
				'data': [...item.data, ...fresh],
				'total': payload?.total ?? item.total,
				'page': nextPage,
				'loading': false,
			}
		}))
	}

	async function reloadColumns(statusIds) {
		const version = filterVersionRef.current

		await Promise.all(statusIds.filter(Boolean).map(async (statusId) => {
			const url = `${apiPrefix}/tasks` + buildParams(filterData, {
				'task_status_id': statusId,
				'page': 0,
				'size': COLUMN_PAGE_SIZE,
			})
			const response = await GET(url, {}, { loader: false })
			loadingColumnsRef.current.delete(statusId)
			if (!httpOk(response) || version !== filterVersionRef.current) return

			const payload = (response.data?.data || []).find(item => item.id === statusId) || response.data?.data?.[0]

			setColumns(prev => prev.map(item => item.id === statusId ? {
				...item,
				'data': payload?.data || [],
				'total': payload?.total ?? item.total,
				'page': 0,
				'loading': false,
			} : item))
		}))
	}

	function patchFilterData(patch) {
		setFilterData(prev => ({ ...prev, ...patch }))
	}

	function handleSearch(search) {
		const nextFilterData = { ...filterData, 'search': search }
		setFilterData(nextFilterData)
		loadBoard(nextFilterData)
	}

	function handleSelectEmployee(option) {
		const nextFilterData = { ...filterData, 'user_id': option ? option.id : null }
		setFilterData(nextFilterData)
		loadBoard(nextFilterData)
	}

	function handleApplyFilters() {
		loadBoard(filterData)
	}

	function handleResetFilters() {
		const nextFilterData = getInitialFilterData()
		setFilterData(nextFilterData)
		setActiveChip('all')
		loadBoard(nextFilterData)
	}

	function handleSelectChip(chipValue, item) {
		var nextFilterData = { ...filterData, 'deadline': null, 'deadline_month': null }

		if (item && item.key === 'month') nextFilterData.deadline_month = item.month
		if (item && item.key !== 'month') nextFilterData.deadline = item.date

		setActiveChip(chipValue)
		setFilterData(nextFilterData)
		loadBoard(nextFilterData)
	}

	// Протяжка доски мышью, как в trello: тянем только за пустое место,
	// у карточек и кнопок остаётся своё поведение
	function handleBoardPointerDown(e) {
		if (e.button !== 0) return
		if (e.target.closest('.tasks-card, button, a, input, textarea, select')) return

		const board = boardRef.current
		if (!board || board.scrollWidth <= board.clientWidth) return

		panRef.current = {
			'pointerId': e.pointerId,
			'startX': e.clientX,
			'scrollLeft': board.scrollLeft,
			'active': false,
		}
	}

	function handleBoardPointerMove(e) {
		const pan = panRef.current
		const board = boardRef.current
		if (!pan || !board) return

		const delta = e.clientX - pan.startX

		// Пока курсор не ушёл на 5px, это ещё клик, а не протяжка
		if (!pan.active) {
			if (Math.abs(delta) < 5) return

			pan.active = true
			setIsPanning(true)
			// Захват, чтобы доска продолжала ехать, даже если курсор ушёл за её пределы
			board.setPointerCapture(pan.pointerId)
		}

		board.scrollLeft = pan.scrollLeft - delta
	}

	function handleBoardPointerUp() {
		const pan = panRef.current
		const board = boardRef.current
		panRef.current = null

		if (!pan || !pan.active) return

		setIsPanning(false)
		if (board?.hasPointerCapture(pan.pointerId)) board.releasePointerCapture(pan.pointerId)
	}

	function handleDragStart(event) {
		setActiveCard(event.active?.data?.current?.task || null)
	}

	function handleDragEnd(event) {
		const { active, over } = event
		setActiveCard(null)

		if (!active || !over) return

		const task = active.data?.current?.task
		const fromStatusId = active.data?.current?.fromStatusId
		const toStatusId = over.data?.current?.statusId

		if (!task || !toStatusId || fromStatusId === toStatusId) return

		const targetColumn = columns.find(item => item.id === toStatusId)
		if (targetColumn && DEADLINE_PROMPT_VALUES.includes(String(targetColumn.value).toLowerCase())) {
			setPendingMove({
				'task': task,
				'fromStatusId': fromStatusId,
				'toStatusId': toStatusId,
				'statusName': targetColumn.name,
				'deadline': task.deadline || '',
				'comment': '',
			})
			setModalTaskStatus(true)
			return
		}

		moveTask({ 'task': task, 'fromStatusId': fromStatusId, 'toStatusId': toStatusId })
	}

	async function moveTask({ task, fromStatusId, toStatusId, deadline = null, comment = null }) {
		const snapshot = columns

		setMovingIds(prev => {
			const next = new Set(prev)
			next.add(task.id)
			return next
		})

		setColumns(prev => prev.map(column => {
			if (column.id === fromStatusId) {
				return {
					...column,
					'data': column.data.filter(item => item.id !== task.id),
					'total': Math.max(0, (column.total || 0) - 1),
				}
			}
			if (column.id === toStatusId) {
				const movedTask = {
					...task,
					'task_status_id': toStatusId,
					...(deadline ? { 'deadline': deadline, 'previous_deadline': task.deadline } : {}),
				}
				return {
					...column,
					'data': [movedTask, ...column.data.filter(item => item.id !== task.id)],
					'total': (column.total || 0) + 1,
				}
			}
			return column
		}))

		var payload = { 'id': task.id, 'task_status_id': toStatusId }
		if (deadline) payload.deadline = deadline
		if (comment) payload.comment = comment

		const response = await PUT(`${apiPrefix}/tasks-status`, payload, { notification: false })

		setMovingIds(prev => {
			const next = new Set(prev)
			next.delete(task.id)
			return next
		})

		if (!httpOk(response)) {
			// 400/403 уже показан интерцептором, откатываем доску
			setColumns(snapshot)
			return
		}

		if (deadline) {
			loadBoard()
			return
		}

		reloadColumns([fromStatusId, toStatusId])
	}

	function submitTaskStatus() {
		if (!pendingMove) return

		moveTask({
			'task': pendingMove.task,
			'fromStatusId': pendingMove.fromStatusId,
			'toStatusId': pendingMove.toStatusId,
			'deadline': pendingMove.deadline,
			'comment': pendingMove.comment,
		})

		toggleModalTaskStatus()
	}

	function toggleModalTaskStatus(bool = false, item = null) {
		setPendingMove(item)
		setModalTaskStatus(bool)
	}

	function toggleModalCreateUpdate(bool = false, item = getDefaultItem()) {
		setSelectedItem(item)
		setModalCreateUpdate(bool)
	}

	function toggleModalDetail(bool = false, item = {}) {
		setDetailItem(item)
		setModalDetail(bool)
	}

	function toggleModalDelete(bool = false, item = {}) {
		setSelectedItem(item)
		setModalDelete(bool)
	}

	function handleEditTask(task) {
		toggleModalDetail()
		toggleModalCreateUpdate(true, {
			...task,
			'user_ids': task.users || [],
			// В задаче точка лежит плоско, а селекту нужен объект опции
			'pos': task.pos_id ? {
				'id': task.pos_id,
				'name': task.pos_name,
				'short_name': task.pos_short_name,
				'login': task.client_login,
			} : null,
		})
	}

	function handleDeleteTask(task) {
		toggleModalDetail()
		toggleModalDelete(true, task)
	}

	// Примечание добавили или убрали в детальной — правим только эту карточку,
	// перезагружать всю доску ради счётчика незачем
	function handleNotesChanged(taskId, notes) {
		// Примечания приходят свежими сверху, поэтому последнее — это первое
		const lastNote = notes.length > 0 ? notes[0].note : null

		setColumns(prev => prev.map(column => ({
			...column,
			'data': column.data.map(item => item.id === taskId
				? { ...item, 'notes_count': notes.length, 'last_note': lastNote }
				: item
			),
		})))
	}

	async function createUpdate(e) {
		e.preventDefault()

		// Автор задачи всегда среди исполнителей, в форме выбирают только «кому ещё»
		var userIds = (selectedItem.user_ids || []).map(item => item.id)
		if (currentUser && !userIds.includes(currentUser.id)) {
			userIds.unshift(currentUser.id)
		}

		var payload = {
			'name': selectedItem.name,
			'description': selectedItem.description,
			'deadline': selectedItem.deadline,
			'category_id': selectedItem.category_id,
			'service_id': selectedItem.service_id,
			'client_login': selectedItem.client_login,
			'user_ids': userIds,
			'pos_id': selectedItem.pos_id || null,
			'client_phone': selectedItem.client_phone || null,
			'director_phone': selectedItem.director_phone || null,
			'address': selectedItem.address || null,
			'direction': selectedItem.direction || null,
			'lead_source': selectedItem.lead_source || null,
		}

		var response
		if (!selectedItem.id) {
			response = await POST(`${apiPrefix}/tasks`, payload, { loader: true })
		} else {
			response = await PUT(`${apiPrefix}/tasks`, { ...payload, 'id': selectedItem.id }, { loader: true })
		}

		if (httpOk(response)) {
			toggleModalCreateUpdate()
			loadBoard()
		}
	}

	async function deleteItem() {
		const response = await DELETE(`${apiPrefix}/tasks/${selectedItem.id}`, {}, { loader: true })

		if (httpOk(response)) {
			toggleModalDelete()
			loadBoard()
		}
	}

	async function importFromExcel(e) {
		const file = e.target.files[0]
		if (!file) return

		var formData = new FormData()
		formData.append('file', file)

		const response = await FILE(`${apiPrefix}/tasks-excel-import`, formData, { loader: true })

		if (httpOk(response)) {
			notify(t('success'), 'success')
			loadBoard()
		}
		// При ошибке сообщение бэкенда («Строка 7: партнёр «99» не найден») уже показал интерцептор

		document.getElementById('tasks-import-btn').value = ''
	}

	async function downloadExcel(isTemplate = false) {
		// Шаблон — тот же файл с одними заголовками, без задач и без фильтров
		const urlParams = isTemplate ? '?template=1' : buildParams(filterData)

		const response = await FILE_D(`${apiPrefix}/tasks-excel` + urlParams, { loader: true })

		if (httpOk(response)) {
			const file = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
			const fileUrl = URL.createObjectURL(file)
			const link = document.createElement('a')
			link.href = fileUrl
			link.setAttribute('download', isTemplate ? 'tasks_template.xlsx' : 'tasks.xlsx')
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			URL.revokeObjectURL(fileUrl)
		}
	}

	function returnKpis() {
		if (columns.length === 0) return []

		const newColumn = columns[0]
		const postponedColumn = columns.find(item => DEADLINE_PROMPT_VALUES.includes(String(item.value).toLowerCase()))
		const completedTotal = columns
			.filter(item => item.is_final)
			.reduce((sum, item) => sum + (item.total || 0), 0)

		var kpis = [
			{
				'label': t('task_total'),
				'value': boardTotal,
				'hint': t('for_period'),
				'color': '#5b73e8',
			},
			{
				'label': newColumn.name,
				'value': newColumn.total || 0,
				'color': newColumn.color || '#f1b44c',
			},
		]

		if (postponedColumn) {
			kpis.push({
				'label': postponedColumn.name,
				'value': postponedColumn.total || 0,
				'color': postponedColumn.color || '#8b5cf6',
			})
		}

		kpis.push({
			'label': t('task_completed'),
			'value': completedTotal,
			'hint': boardTotal > 0 ? Math.round((completedTotal / boardTotal) * 100) + '%' : '',
			'color': '#34c38f',
		})

		return kpis
	}

	async function getStatuses() {
		const response = await GET(`${apiPrefix}/helper/task-statuses`, {}, { loader: false })
		if (httpOk(response)) {
			const statusList = response.data || []
			setStatuses(statusList)
			return statusList
		}
		return []
	}

	async function getCategories() {
		const response = await GET(`${apiPrefix}/helper/task-categories`, {}, { loader: false })
		if (httpOk(response)) setCategories(response.data || [])
	}

	async function getPartners() {
		const response = await GET(`${apiPrefix}/helper/task-partners`, {}, { loader: false })
		if (httpOk(response)) setPartners(response.data || [])
	}

	async function getData() {
		const statusList = await getStatuses()
		getCategories()
		getPartners()
		loadBoard(getInitialFilterData(), statusList)
	}

	useEffect(() => {
		getData()
	}, [apiPrefix]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="vertical-center">
					<h4>{t(titleKey)} [{boardTotal}]</h4>
				</div>
			</div>

			<TasksBoardToolbar
				apiPrefix={apiPrefix}
				filterData={filterData}
				patchFilterData={patchFilterData}
				partners={partners}
				categories={categories}
				dropdownIsOpen={dropdownIsOpen}
				setDropdownIsOpen={setDropdownIsOpen}
				kpis={returnKpis()}
				deadlineCounts={deadlineCounts}
				activeChip={activeChip}
				boardTotal={boardTotal}
				categoriesPath={categoriesPath}
				onSearchChange={handleSearch}
				onSelectEmployee={handleSelectEmployee}
				onApplyFilters={handleApplyFilters}
				onResetFilters={handleResetFilters}
				onSelectChip={handleSelectChip}
				onCreate={() => toggleModalCreateUpdate(true)}
				onImport={importFromExcel}
				onExport={() => downloadExcel()}
				onDownloadTemplate={() => downloadExcel(true)}>
			</TasksBoardToolbar>

			<DndContext
				sensors={sensors}
				collisionDetection={closestCorners}
				onDragStart={handleDragStart}
				onDragCancel={() => setActiveCard(null)}
				onDragEnd={handleDragEnd}>

				<div ref={boardRef}
					className={'tasks-board' + (isPanning ? ' is-panning' : '')}
					onPointerDown={handleBoardPointerDown}
					onPointerMove={handleBoardPointerMove}
					onPointerUp={handleBoardPointerUp}
					onPointerCancel={handleBoardPointerUp}>

					{columns.map((column) => (
						<TasksBoardColumn key={column.id}
							column={column}
							movingIds={movingIds}
							canManageTask={canManageTask}
							canEditTask={canEdit}
							onCreate={() => toggleModalCreateUpdate(true)}
							onLoadMore={loadColumnPage}
							onOpenDetail={(task) => toggleModalDetail(true, task)}
							onEdit={handleEditTask}
							onDelete={handleDeleteTask}>
						</TasksBoardColumn>
					))}
				</div>

				<DragOverlay dropAnimation={null}>
					{activeCard ?
						<TasksBoardCard task={activeCard} isOverlay={true}></TasksBoardCard>
						: null
					}
				</DragOverlay>
			</DndContext>

			{modalCreateUpdate &&
				<ModalCreateUpdate
					apiPrefix={apiPrefix}
					categories={categories}
					partners={partners}
					currentUser={currentUser}
					canManageExecutors={!selectedItem.id || canManageTask(selectedItem)}
					selectedItem={selectedItem}
					setSelectedItem={setSelectedItem}
					createUpdate={createUpdate}
					modalCreateUpdate={modalCreateUpdate}
					toggleModalCreateUpdate={toggleModalCreateUpdate}>
				</ModalCreateUpdate>
			}

			{modalTaskStatus &&
				<ModalTaskStatus
					pendingMove={pendingMove}
					setPendingMove={setPendingMove}
					submitTaskStatus={submitTaskStatus}
					modalTaskStatus={modalTaskStatus}
					toggleModalTaskStatus={toggleModalTaskStatus}>
				</ModalTaskStatus>
			}

			{modalDetail &&
				<ModalTaskDetail
					apiPrefix={apiPrefix}
					detailItem={detailItem}
					statuses={statuses}
					canManage={canManageTask(detailItem)}
					canEdit={canEdit(detailItem)}
					currentUserLogin={currentUserLogin}
					onEdit={handleEditTask}
					onDelete={handleDeleteTask}
					onNotesChanged={handleNotesChanged}
					modalDetail={modalDetail}
					toggleModalDetail={toggleModalDetail}>
				</ModalTaskDetail>
			}

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}>
			</DeleteModal>
		</>
	)
}

export default TasksBoardView
